import { FunctionComponent, MouseEventHandler, PropsWithChildren } from "react";
import { generatePath } from "react-router-dom";
import React from "react";
import { IconSpinner } from "@web-monorepo/dds-icons";
import clsx from "clsx";
import styles from "./DDSButton.module.css";

export const Sizes = ["large", "medium", "small", "xsmall"] as const;
export const Variants = [
  "primary",
  "primarySubtle",
  "secondary",
  "tertiary",
  "gray",
  "destructive",
  "destructiveTertiary",
  "ghost",
  "unstyled",
  "menuItem",
  "destructiveMenuItem",
  "plusGradient",
] as const;
export const Widths = ["fit", "fill"] as const;

type HTMLButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>;

type BaseProps = PropsWithChildren<{
  ["data-name"]: string;
  size?: (typeof Sizes)[number];
  variant?: (typeof Variants)[number];
  width?: (typeof Widths)[number];
  experiments?: string[];
  className?: string;
  loading?: boolean;
  active?: boolean;
}>;

type ButtonProps = BaseProps &
  Pick<
    HTMLButtonProps,
    | "type"
    | "autoFocus"
    | "tabIndex"
    | "form"
    | "onMouseDown"
    | "onBlur"
    | "title"
    | "role"
    | "aria-selected"
    | "aria-describedby"
    | "aria-label"
  > & {
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    className?: string;
    "disable-tag-translation"?: boolean | string;
  };

export const Buttonstyles = styles;
export const Variantstyles = Object.fromEntries(Variants.map((v) => [v, styles[v]]));

const ButtonSizeToSpinnerSize: Record<(typeof Sizes)[number], "s" | "m" | "l"> = {
  large: "l",
  medium: "m",
  small: "s",
  xsmall: "s",
};

export const DDSButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      onClick,
      children,
      size = "medium",
      variant = "primary",
      disabled = false,
      type = "button",
      ["data-name"]: dataName,
      width,
      experiments,
      className: classnameProp,
      loading = false,
      active = false,
      "aria-describedby": ariaDescribedBy,
      "disable-tag-translation": _disableTagTranslation,
      ...rest
    },
    ref,
  ) => {
    const className =
      variant === "unstyled"
        ? clsx(styles.unstyled, classnameProp)
        : clsx(
            styles.button,
            styles[size],
            styles[variant],
            width && styles[width],
            loading && styles.loading,
            active && styles.active,
            classnameProp,
          );

    const content =
      variant !== "unstyled" ? (
        <>
          {loading && (
            <IconSpinner aria-label="Loading" className={styles.spinner} size={ButtonSizeToSpinnerSize[size]} />
          )}
          <span aria-hidden={loading ? true : undefined} className={styles.children}>
            {children}
          </span>
        </>
      ) : (
        children
      );

    return (
      <button
        data-name={dataName}
        data-experiments={experiments}
        type={type}
        className={className}
        onClick={(e) => {
          if (loading) {
            e.preventDefault();
            return;
          }

          onClick?.(e);
        }}
        disabled={disabled}
        aria-disabled={disabled || loading}
        ref={ref}
        aria-busy={loading}
        {...rest}
        aria-describedby={ariaDescribedBy}
      >
        {content}
      </button>
    );
  },
);

type Params = Record<string, string | null>;

type AnchorProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  "children" | "className" | "href" | "onClick" | "rel" | "target"
>;

type LinkProps<T extends string> = BaseProps &
  AnchorProps & {
    onClick?: MouseEventHandler<HTMLAnchorElement>;
    "aria-current"?: React.AriaAttributes["aria-current"];
  } & (
    | {
        to: T;
        search?: string;
        params?: Params;
        target?: string;
      }
    | { href: string; target?: string; rel?: string }
  );

// Helper type to extract the props of a React component
type ComponentProps<T> = T extends React.ComponentType<infer P> ? P : never;

export function LinkButtonFactory<
  T extends FunctionComponent<
    PropsWithChildren<{
      to: string;
      search?: string;
      params?: Params;
      target?: string;
      onClick?: React.MouseEventHandler;
      rel?: string;
    }>
  >,
>(LinkType: T) {
  const Link = LinkType as React.ElementType;
  return React.forwardRef<HTMLAnchorElement, LinkProps<ComponentProps<T>["to"]>>(function LinkButton(
    {
      children,
      size = "medium",
      variant = "primary",
      width = "fit",
      ["data-name"]: dataName,
      experiments,
      loading: _loading,
      active: _active,
      target,
      className: classNameProp,
      onClick,
      "aria-current": ariaCurrent,
      ...rest
    },
    ref,
  ) {
    const rel = "rel" in rest ? rest.rel : undefined;
    const className = [styles.button, styles[size], styles[variant], styles[width], classNameProp].join(" ");

    if ("to" in rest) {
      const { to, search, params, ...anchorProps } = rest;
      const path = generatePath(to as string, params);

      return (
        <Link
          {...anchorProps}
          ref={ref}
          className={className}
          to={path}
          search={search}
          target={target}
          data-name={dataName}
          experiments={experiments}
          onClick={onClick}
          aria-current={ariaCurrent}
        >
          {children}
        </Link>
      );
    }

    const { href, ...anchorProps } = rest;
    return (
      <a
        {...anchorProps}
        ref={ref}
        href={href || undefined}
        className={className}
        data-name={dataName}
        data-experiments={experiments}
        target={target}
        onClick={onClick}
        rel={rel}
        aria-current={ariaCurrent}
      >
        {children}
      </a>
    );
  });
}
